import { z } from "zod";

import { scriptProviderSchema } from "./scriptProvider";

const optionalSiteIdSchema = z
  .string()
  .min(1)
  .optional()
  .describe(
    "Site id from list_sites. Omit to target the project's current site.",
  );
const optionalSiteArgsSchema = z
  .object({ siteId: optionalSiteIdSchema })
  .default({});

// NOTE (Ezra, 2026-06-08): Views are agent-facing product destinations.
// The manage-tabs tool wraps them in open/close operations, and editor code
// converts them to browser tabs.
const viewSchemas = [
  z.object({
    viewId: z.literal("home").describe("Open the agent home view."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z
      .literal("content.sites")
      .describe("Open the Content site picker."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z
      .literal("content.site")
      .describe("Open a site's content collections."),
    args: z
      .object({
        siteId: z.string().min(1).describe("Site id from list_sites."),
      })
      .describe("Content site arguments."),
  }),
  z.object({
    viewId: z
      .literal("content.collection")
      .describe("Open a content collection for a specific site."),
    args: z
      .object({
        siteId: z.string().min(1).describe("Site id from list_sites."),
        collectionSlug: z.string().min(1).describe("Collection URL slug."),
      })
      .describe("content collection arguments."),
  }),
  z.object({
    viewId: z
      .literal("content.entry")
      .describe("Open a content entry for a specific site."),
    args: z
      .object({
        siteId: z.string().min(1).describe("Site id from list_sites."),
        collectionSlug: z.string().min(1).describe("Collection URL slug."),
        entrySlug: z.string().min(1).describe("Entry URL slug."),
      })
      .describe("content entry arguments."),
  }),
  z.object({
    viewId: z.literal("assets.home").describe("Open the Assets library."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z.literal("assets.detail").describe("Open a specific asset by ID."),
    args: z
      .object({
        assetId: z.string().min(1).describe("Asset ID."),
      })
      .describe("Assets detail arguments."),
  }),
  z.object({
    viewId: z.literal("files.home").describe("Open the Files app."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z
      .literal("files.view")
      .describe("Open one of the Files app root views."),
    args: z
      .object({
        view: z
          .enum(["home", "team", "shared"])
          .describe("Files route view: home, team, or shared."),
      })
      .describe("Files root-view arguments."),
  }),
  z.object({
    viewId: z
      .literal("files.folder")
      .describe("Open a specific folder in the Files app."),
    args: z
      .object({
        view: z
          .enum(["home", "team", "shared"])
          .describe("Files root view containing the folder."),
        folderId: z.string().min(1).describe("Folder ID."),
      })
      .describe("Files folder arguments."),
  }),
  z.object({
    viewId: z
      .literal("files.detail")
      .describe("Open a specific file detail view in the Files app."),
    args: z
      .object({
        view: z
          .enum(["home", "team", "shared"])
          .describe("Files root view containing the file."),
        folderId: z
          .string()
          .min(1)
          .optional()
          .describe("Containing folder ID, omitted for root files."),
        fileId: z.string().min(1).describe("File ID."),
      })
      .describe("Files detail arguments."),
  }),
  z.object({
    viewId: z
      .literal("siteBuilder.dashboard")
      .describe("Open a Site Builder site dashboard."),
    args: optionalSiteArgsSchema.describe("Site dashboard arguments."),
  }),
  z.object({
    viewId: z
      .literal("siteBuilder.settings")
      .describe("Open Site Builder settings."),
    args: optionalSiteArgsSchema.describe("Site Builder settings arguments."),
  }),
  z.object({
    viewId: z
      .literal("siteBuilder.domains")
      .describe("Open the domains tab of Site Builder settings."),
    args: optionalSiteArgsSchema.describe("Site Builder domains arguments."),
  }),
  z.object({
    viewId: z
      .literal("siteBuilder.page")
      .describe("Open a Site Builder page by public page route."),
    args: z
      .object({
        route: z
          .string()
          .min(1)
          .describe("Public page route, for example /products."),
        siteId: optionalSiteIdSchema,
      })
      .describe("Site Builder page arguments."),
  }),
  z.object({
    viewId: z
      .literal("siteBuilder.components")
      .describe(
        "Open the Site Components view in Site Builder, where the user previews and customizes the reusable components in the site's components/ directory.",
      ),
    args: optionalSiteArgsSchema.describe("Site Components arguments."),
  }),
  z.object({
    viewId: z
      .literal("siteBuilder.templates")
      .describe("Open the page template gallery in Site Builder."),
    args: optionalSiteArgsSchema.describe("Page template gallery arguments."),
  }),
  z.object({
    viewId: z
      .literal("siteBuilder.templateDetail")
      .describe("Open a specific page template in Site Builder."),
    args: z
      .object({
        templateId: z.string().min(1).describe("Registry page template ID."),
        siteId: optionalSiteIdSchema,
      })
      .describe("Page template detail arguments."),
  }),
  z.object({
    viewId: z
      .literal("siteBuilder.translations")
      .describe("Open the Site Builder translations workspace."),
    args: optionalSiteArgsSchema.describe("Translations arguments."),
  }),
  z.object({
    viewId: z
      .literal("siteBuilder.newSite")
      .describe("Open the focused site creation screen."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z
      .literal("siteBuilder.scripts")
      .describe(
        "Open the Scripts & Pixels tab in Site Builder Site Settings, where tracking scripts and marketing pixels (Meta, TikTok, GA4, etc.) are managed.",
      ),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z
      .literal("siteBuilder.scriptDetail")
      .describe(
        "Open a specific tracking script or marketing pixel provider in Site Builder Site Settings.",
      ),
    args: z
      .object({
        provider: scriptProviderSchema.describe(
          "Script provider, for example Meta, TikTok, or GA4.",
        ),
      })
      .describe("Script provider detail arguments."),
  }),
  z.object({
    viewId: z.literal("brandStudio.home").describe("Open Brand Studio."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z
      .literal("brandStudio.project")
      .describe("Open the project brand in Brand Studio."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z
      .literal("brandStudio.brand")
      .describe("Open a specific Brand Studio brand by slug."),
    args: z
      .object({
        slug: z.string().min(1).describe("Brand slug."),
      })
      .describe("Brand Studio brand arguments."),
  }),
  z.object({
    viewId: z.literal("skills.home").describe("Open the Skills home page."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z
      .literal("skills.mySkills")
      .describe("Show the user's installed skills."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z.literal("skills.new").describe("Create a new skill."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z
      .literal("skills.detail")
      .describe(
        "Open a skill by slug in the Skills app. This works for installed workspace skills and uninstalled library skills; if both exist, the Skills app shows the installed workspace copy.",
      ),
    args: z
      .object({
        slug: z
          .string()
          .min(1)
          .describe(
            "Skill slug, for example yuxin-plan. For library skills, use the registry item slug or handle.",
          ),
      })
      .describe("Skill detail arguments."),
  }),
  z.object({
    viewId: z.literal("skills.edit").describe("Edit a specific skill by slug."),
    args: z
      .object({
        slug: z.string().min(1).describe("Skill slug, for example yuxin-plan."),
      })
      .describe("Skill edit arguments."),
  }),
  z.object({
    viewId: z
      .literal("integrations.home")
      .describe("Open the Integrations home page."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z
      .literal("integrations.enabled")
      .describe("Show enabled integrations."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z
      .literal("integrations.agentDetail")
      .describe("Open an agent integration detail page."),
    args: z
      .object({
        integrationKey: z
          .string()
          .min(1)
          .describe("Connected integration key, for example shopify."),
      })
      .describe("Agent integration detail arguments."),
  }),
  z.object({
    viewId: z.literal("products.home").describe("Open the Products home page."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z
      .literal("products.create")
      .describe(
        "Open the Products new-product hub (create manually, choose from the catalog, or import a CSV).",
      ),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z.literal("tasks.home").describe("Open the Tasks home view."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z.literal("tasks.plan").describe("Open the Tasks planning view."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z.literal("tasks.history").describe("Open the Tasks run history."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z.literal("tasks.new").describe("Create a new task."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z.literal("tasks.detail").describe("Open a specific task by ID."),
    args: z
      .object({
        taskId: z.string().min(1).describe("Task ID."),
      })
      .describe("Task detail arguments."),
  }),
  z.object({
    viewId: z.literal("tasks.run").describe("Open a specific task run by ID."),
    args: z
      .object({
        runId: z.string().min(1).describe("Task run ID."),
      })
      .describe("Task run arguments."),
  }),
  z.object({
    viewId: z.literal("plans.home").describe("Open the Plans home page."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z.literal("plans.detail").describe("Open a specific plan by slug."),
    args: z
      .object({
        planSlug: z
          .string()
          .min(1)
          .describe("Plan slug, for example launch-checklist."),
      })
      .describe("Plans detail arguments."),
  }),
  z.object({
    viewId: z.literal("insights.home").describe("Open the Insights home page."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z
      .literal("insights.dashboard")
      .describe("Open an Insights dashboard by filename."),
    args: z
      .object({
        dashboardFile: z
          .string()
          .min(1)
          .describe("Dashboard filename, for example default.insights.json."),
      })
      .describe("Insights dashboard arguments."),
  }),
  z.object({
    viewId: z
      .literal("draw.gallery")
      .describe("Open the Draw gallery of all .tldr drawings."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z
      .literal("draw.canvas")
      .describe("Open a specific drawing on the Draw canvas by its file ID."),
    args: z
      .object({
        fileId: z.string().min(1).describe("The .tldr Drive file ID to open."),
      })
      .describe("Draw canvas arguments."),
  }),
  z.object({
    viewId: z.literal("content.home").describe("Open the Content app."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z
      .literal("orders.emails")
      .describe("Open customer email settings in the Orders app."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z.literal("settings").describe("Open project settings."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z
      .literal("settings.checkout")
      .describe("Open project checkout settings."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z
      .literal("settings.storeProfile")
      .describe("Open the store profile section in Commerce settings."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z
      .literal("settings.orderNotifications")
      .describe("Open the order notifications section in Commerce settings."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z
      .literal("settings.personalize")
      .describe("Open project personalization settings."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z
      .literal("settings.permissions")
      .describe("Open agent and workspace permissions settings."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z
      .literal("settings.workspace")
      .describe("Open workspace settings."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z
      .literal("settings.members")
      .describe("Open team member settings."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z.literal("settings.billing").describe("Open billing settings."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z.literal("settings.usage").describe("Open usage settings."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z.literal("settings.account").describe("Open account settings."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z
      .literal("settings.connectedApps")
      .describe("Open connected app settings."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z.literal("settings.referrals").describe("Open referral settings."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
  z.object({
    viewId: z
      .literal("settings.apiTokens")
      .describe("Open API token settings."),
    args: z.object({}).default({}).describe("No arguments."),
  }),
] as const;

const viewIds = viewSchemas.map((schema) => {
  return schema.shape.viewId.value;
});

export const viewIdSchema = z.enum(viewIds).describe("ViewId");

export const viewSchema = z
  .discriminatedUnion("viewId", viewSchemas)
  .describe("View");
